import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';

const CATEGORIES = {
  technical_support: { label: 'Technical Support', color: 'bg-blue-100 text-blue-800' },
  feature_request: { label: 'Feature Request', color: 'bg-purple-100 text-purple-800' },
  business_inquiry: { label: 'Business Inquiry', color: 'bg-green-100 text-green-800' },
  general_question: { label: 'General Question', color: 'bg-gray-100 text-gray-800' },
  bug_report: { label: 'Bug Report', color: 'bg-red-100 text-red-800' },
};

const STATUSES = {
  new: { label: 'New', color: 'bg-yellow-100 text-yellow-800' },
  read: { label: 'Read', color: 'bg-blue-100 text-blue-800' },
  resolved: { label: 'Resolved', color: 'bg-green-100 text-green-800' },
};

function CommunicationsDashboard() {
  const { isAdmin } = useAuth();
  const [submissions, setSubmissions] = useState([]);
  const [counts, setCounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filterCategory, setFilterCategory] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [adminNotes, setAdminNotes] = useState('');
  const [updating, setUpdating] = useState(false);

  const fetchSubmissions = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filterCategory) params.set('category', filterCategory);
      if (filterStatus) params.set('status', filterStatus);
      params.set('limit', '100');

      const res = await fetch(`/api/admin/contacts?${params}`, { credentials: 'include' });
      const data = await res.json();

      if (data.success) {
        setSubmissions(data.submissions);
        setCounts(data.counts);
        setError(null);
      } else {
        setError(data.error || 'Failed to fetch submissions');
      }
    } catch {
      setError('Network error');
    } finally {
      setLoading(false);
    }
  }, [filterCategory, filterStatus]);

  useEffect(() => {
    if (isAdmin) fetchSubmissions();
  }, [isAdmin, fetchSubmissions]);

  const getCount = (status) => {
    const found = counts.find(c => c.status === status);
    return found ? found.count : 0;
  };

  const totalCount = counts.reduce((sum, c) => sum + c.count, 0);

  const handleStatusUpdate = async (uuid, newStatus) => {
    setUpdating(true);
    try {
      const res = await fetch(`/api/admin/contacts/${uuid}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ status: newStatus, admin_notes: adminNotes || undefined }),
      });
      const data = await res.json();
      if (data.success) {
        setSubmissions(prev => prev.map(s => s.uuid === uuid ? data.submission : s));
        if (selectedSubmission?.uuid === uuid) {
          setSelectedSubmission(data.submission);
        }
        fetchSubmissions();
      }
    } catch {
      // silent
    } finally {
      setUpdating(false);
    }
  };

  const openDetail = (submission) => {
    setSelectedSubmission(submission);
    setAdminNotes(submission.admin_notes || '');
    if (submission.status === 'new') {
      handleStatusUpdate(submission.uuid, 'read');
    }
  };

  const formatDate = (dateStr) => {
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
  };

  if (!isAdmin) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center">
        <h2 className="text-2xl font-bold text-gray-900">Access Denied</h2>
        <p className="mt-2 text-gray-600">You need admin privileges to view this page.</p>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">Communications</h1>
        <p className="mt-1 text-sm text-gray-500">Contact form submissions from the landing page</p>
      </div>

      {/* Status counts */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <p className="text-sm text-gray-500">Total</p>
          <p className="text-2xl font-bold text-gray-900">{totalCount}</p>
        </div>
        <div className="bg-white rounded-lg border border-yellow-200 p-4">
          <p className="text-sm text-yellow-600">New</p>
          <p className="text-2xl font-bold text-yellow-700">{getCount('new')}</p>
        </div>
        <div className="bg-white rounded-lg border border-blue-200 p-4">
          <p className="text-sm text-blue-600">Read</p>
          <p className="text-2xl font-bold text-blue-700">{getCount('read')}</p>
        </div>
        <div className="bg-white rounded-lg border border-green-200 p-4">
          <p className="text-sm text-green-600">Resolved</p>
          <p className="text-2xl font-bold text-green-700">{getCount('resolved')}</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-4 mb-6">
        <select
          value={filterCategory}
          onChange={e => setFilterCategory(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
        >
          <option value="">All Categories</option>
          {Object.entries(CATEGORIES).map(([val, { label }]) => (
            <option key={val} value={val}>{label}</option>
          ))}
        </select>
        <select
          value={filterStatus}
          onChange={e => setFilterStatus(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
        >
          <option value="">All Statuses</option>
          {Object.entries(STATUSES).map(([val, { label }]) => (
            <option key={val} value={val}>{label}</option>
          ))}
        </select>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-50 text-red-800 rounded-lg border border-red-200 text-sm">{error}</div>
      )}

      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-sm text-gray-500">Loading submissions...</p>
        </div>
      ) : submissions.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
          <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
          </svg>
          <p className="mt-4 text-gray-500">No submissions found</p>
        </div>
      ) : (
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">From</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Category</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Subject</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {submissions.map(sub => (
                <tr
                  key={sub.uuid}
                  onClick={() => openDetail(sub)}
                  className={`cursor-pointer hover:bg-gray-50 transition-colors ${sub.status === 'new' ? 'bg-yellow-50/50' : ''}`}
                >
                  <td className="px-6 py-4">
                    <div className="text-sm font-medium text-gray-900">{sub.name}</div>
                    <div className="text-xs text-gray-500">{sub.email}</div>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${CATEGORIES[sub.category]?.color || 'bg-gray-100 text-gray-800'}`}>
                      {CATEGORIES[sub.category]?.label || sub.category}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-900 max-w-xs truncate">{sub.subject}</td>
                  <td className="px-6 py-4">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${STATUSES[sub.status]?.color || 'bg-gray-100 text-gray-800'}`}>
                      {STATUSES[sub.status]?.label || sub.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500 whitespace-nowrap">{formatDate(sub.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Detail Modal */}
      {selectedSubmission && (
        <>
          <div className="fixed inset-0 bg-black bg-opacity-50 z-40" onClick={() => setSelectedSubmission(null)} />
          <div className="fixed inset-y-0 right-0 w-full max-w-lg bg-white shadow-xl z-50 overflow-y-auto">
            <div className="p-6">
              {/* Header */}
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-lg font-semibold text-gray-900">Message Details</h2>
                <button
                  onClick={() => setSelectedSubmission(null)}
                  className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
                >
                  <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Badges */}
              <div className="flex gap-2 mb-6">
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${CATEGORIES[selectedSubmission.category]?.color || ''}`}>
                  {CATEGORIES[selectedSubmission.category]?.label || selectedSubmission.category}
                </span>
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${STATUSES[selectedSubmission.status]?.color || ''}`}>
                  {STATUSES[selectedSubmission.status]?.label || selectedSubmission.status}
                </span>
              </div>

              {/* Info */}
              <div className="space-y-4 mb-6">
                <div>
                  <label className="text-xs font-medium text-gray-500 uppercase">From</label>
                  <p className="text-sm text-gray-900">{selectedSubmission.name}</p>
                  <a href={`mailto:${selectedSubmission.email}`} className="text-sm text-primary-600 hover:text-primary-700">{selectedSubmission.email}</a>
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-500 uppercase">Subject</label>
                  <p className="text-sm text-gray-900">{selectedSubmission.subject}</p>
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-500 uppercase">Date</label>
                  <p className="text-sm text-gray-900">{formatDate(selectedSubmission.created_at)}</p>
                </div>
              </div>

              {/* Message */}
              <div className="mb-6">
                <label className="text-xs font-medium text-gray-500 uppercase">Message</label>
                <div className="mt-2 p-4 bg-gray-50 rounded-lg text-sm text-gray-800 whitespace-pre-wrap">
                  {selectedSubmission.message}
                </div>
              </div>

              {/* Status update */}
              <div className="border-t border-gray-200 pt-6">
                <label className="text-xs font-medium text-gray-500 uppercase">Update Status</label>
                <div className="flex gap-2 mt-2">
                  {Object.entries(STATUSES).map(([val, { label }]) => (
                    <button
                      key={val}
                      onClick={() => handleStatusUpdate(selectedSubmission.uuid, val)}
                      disabled={updating || selectedSubmission.status === val}
                      className={`px-3 py-1.5 text-xs font-medium rounded-lg border transition-colors ${
                        selectedSubmission.status === val
                          ? 'bg-primary-50 border-primary-300 text-primary-700'
                          : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                      } disabled:opacity-50`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Admin notes */}
              <div className="mt-6">
                <label className="text-xs font-medium text-gray-500 uppercase">Admin Notes</label>
                <textarea
                  value={adminNotes}
                  onChange={e => setAdminNotes(e.target.value)}
                  rows={3}
                  className="mt-2 w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 resize-vertical"
                  placeholder="Internal notes..."
                />
                <button
                  onClick={() => handleStatusUpdate(selectedSubmission.uuid, selectedSubmission.status)}
                  disabled={updating}
                  className="mt-2 px-4 py-2 bg-gray-900 text-white text-sm font-medium rounded-lg hover:bg-gray-800 disabled:opacity-50 transition-colors"
                >
                  {updating ? 'Saving...' : 'Save Notes'}
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default CommunicationsDashboard;
